import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Select, Option, Checkbox } from '@vmos2/design-system';
import isNil from 'lodash/isNil';
import get from 'lodash/get';

type Props = {
  className?: string,
  setting: {
    options: number[],
    default: number,
  },
  selectedOption: number,
  readOnly?: boolean,
  onChange: (value: number | null) => void,
};

export const ServiceChargePicker = ({
  className,
  readOnly,
  setting: { options, default: defChoice },
  selectedOption,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  const active = !!selectedOption;
  const [isOpen, setOpenStatus] = useState(true);
  const initial = isNil(selectedOption) ? defChoice || get(options, 0, 0) : selectedOption;
  const multipleOptions = options.length > 1;
  const selectedOptionString = selectedOption ? selectedOption.toString() : initial.toString();

  useEffect(() => {
    if (isOpen && !readOnly && defChoice && isNil(selectedOption)) {
      onChange(defChoice);
    }
  }, [defChoice, readOnly, selectedOption, isOpen]);

  useEffect(() => {
    if (initial > 0 && isOpen) {
      onChange(initial);
    }
  }, [initial, isOpen]);

  useEffect(() => {
    if (isOpen && selectedOption) {
      setOpenStatus(true);
    }
  }, [selectedOption, setOpenStatus, isOpen]);

  const handleChange = useCallback(
    e => {
      onChange(e.target.checked ? initial : null);
      setOpenStatus(!isOpen);
    },
    [setOpenStatus, isOpen, initial]
  );

  if (readOnly) {
    return (
      <div className={className}>
        {t('payment.orderSummary:serviceChargeWithAmount', { serviceCharge: `${selectedOptionString}%` })}
      </div>
    );
  }

  if (multipleOptions) {
    return (
      <div className={className}>
        <InlineCheckbox
          checked={isOpen}
          onChange={handleChange}
          label={<Label>{t('payment.orderSummary:optionalServiceChargeSimple')}</Label>}
        />
        {isOpen && (
          <SelectWrapper defaultValue={initial} onChange={({ target }) => onChange(+target.value)}>
            {options.map(item => (
              <Option value={item} key={String(item)}>
                {item}%
              </Option>
            ))}
          </SelectWrapper>
        )}
      </div>
    );
  }

  // Single choice On/Off Mode
  return (
    <Wrapper className={className}>
      <InlineCheckbox
        checked={active}
        onChange={({ target }) => onChange(target.checked ? initial : null)}
        label={
          <Label>
            {t('payment.orderSummary:optionalServiceChargeWithAmount', { serviceCharge: `${selectedOptionString}%` })}
          </Label>
        }
      />
    </Wrapper>
  );
};

ServiceChargePicker.defaultProps = {
  className: '',
  readOnly: true,
  setting: {
    options: [],
  },
};

const Wrapper = styled('div')`
  display: flex;
`;

const SelectWrapper = styled(Select)`
  margin-top: 8px;
`;

const InlineCheckbox = styled(Checkbox)`
  display: inline-flex;
  flex-direction: row-reverse;
`;

const Label = styled('span')`
  margin-right: 8px;
  margin-left: -8px;
  font-size: ${({ theme }) => theme.fontSize.md};
  @media (${({ theme }) => theme.breakpoint.desktop}) {
    font-size: ${({ theme }) => theme.fontSize.xl};
  }
`;
